import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { CheckCircle, FileText, Loader2, MessageSquare, Save, Scale, User, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { FormActions, FormField, FormPageHeader, FormSection } from '@/components/common/FormSection'
import { TagsInput } from '@/components/common/TagsInput'
import { getClientDisplayName, interactionTypeLabels, type InteractionType } from '@/components/interactions/InteractionBadges'
import { clientService } from '../services/client-service'
import { Client, ClientInteraction } from '../types/client'
import { Case } from '../types/case'
import { caseService } from '../services/case-service'
import { getErrorMessage } from '../utils/error.utils'

type CreateInteractionPayload = Omit<ClientInteraction, 'id' | 'clientId' | 'createdAt' | 'createdBy'>

interface CreateInteractionFormData {
  clientId: string
  type: InteractionType
  subject: string
  notes: string // Merged description + outcome
  linkedCaseId: string
  tags: string[]
}

export function CreateInteractionPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const clientIdParam = searchParams.get('clientId')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [clientNameInput, setClientNameInput] = useState('')
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [cases, setCases] = useState<Case[]>([])
  const [caseSearchInput, setCaseSearchInput] = useState('')
  const [showCaseDropdown, setShowCaseDropdown] = useState(false)
  const [selectedCase, setSelectedCase] = useState<Case | null>(null)

  // Modal de sucesso pós-salvamento
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [savedClientId, setSavedClientId] = useState<string | null>(null)
  const [wasNewClientCreated, setWasNewClientCreated] = useState(false)

  const [formData, setFormData] = useState<CreateInteractionFormData>({
    clientId: clientIdParam || '',
    type: 'meeting',
    subject: '',
    notes: '',
    linkedCaseId: '',
    tags: []
  })

  useEffect(() => {
    loadClients()
    loadCases()
  }, [])

  const loadClientById = useCallback(async (clientId: string) => {
    try {
      const client = await clientService.getClientById(clientId)
      setSelectedClient(client)
      setClientNameInput(getClientDisplayName(client))
      setFormData(prev => ({ ...prev, clientId }))
    } catch (err) {
      console.error('Error loading client:', err)
    }
  }, [])

  useEffect(() => {
    if (clientIdParam) {
      loadClientById(clientIdParam)
    }
  }, [clientIdParam, loadClientById])

  const loadClients = async () => {
    try {
      const response = await clientService.getAllClients({}, { page: 1, limit: 100 })
      setClients(response.clients)
    } catch (err) {
      setError('Erro ao carregar contatos')
    }
  }

  const loadCases = async () => {
    try {
      const response = await caseService.getAllCases({}, { page: 1, limit: 100, sort: 'createdAt:desc' })
      setCases(response.cases)
    } catch (err) {
      setError('Erro ao carregar casos')
    }
  }

  const filteredClients = clients.filter(client => {
    const displayName = getClientDisplayName(client).toLowerCase()
    return displayName.includes(clientNameInput.toLowerCase())
  })

  const filteredCases = cases.filter(c => {
    // Filtrar apenas casos do cliente selecionado
    if (formData.clientId && c.clientId !== formData.clientId) return false

    // Filtrar por busca de texto
    if (!caseSearchInput) return true
    const searchLower = caseSearchInput.toLowerCase()
    return c.title.toLowerCase().includes(searchLower) ||
           c.caseNumber.toLowerCase().includes(searchLower) ||
           c.internalNumber.toLowerCase().includes(searchLower)
  })

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client)
    setClientNameInput(getClientDisplayName(client))
    setFormData(prev => ({ ...prev, clientId: client.id }))
    setShowClientDropdown(false)
  }

  const handleCaseSelect = (caseItem: Case) => {
    setSelectedCase(caseItem)
    setCaseSearchInput(`${caseItem.caseNumber} - ${caseItem.title}`)
    setFormData(prev => ({ ...prev, linkedCaseId: caseItem.id }))
    setShowCaseDropdown(false)
  }

  const handleInputChange = (field: keyof CreateInteractionFormData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleAddTag = (raw: string) => {
    const tag = raw.trim()
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }))
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!clientNameInput.trim()) {
      setError('Digite o nome do contato')
      return
    }

    if (!formData.subject.trim()) {
      setError('O assunto é obrigatório')
      return
    }

    setLoading(true)
    setError(null)

    try {
      let clientId = formData.clientId
      let isNewClient = false

      // Se não houver um cliente selecionado, criar um novo (cadastro simplificado)
      if (!clientId) {
        const [firstName, ...lastNameParts] = clientNameInput.trim().split(/\s+/)
        const newClient = await clientService.createClient({
          clientType: 'PERSON',
          personalData: {
            fullName: clientNameInput.trim(),
            firstName,
            lastName: lastNameParts.join(' '),
            nationality: 'Brasileiro',
            profession: undefined,
            cpf: undefined, // Não enviar CPF vazio, apenas undefined
            rg: undefined
          },
          contacts: [],
          status: 'ACTIVE'
        })
        clientId = newClient.id
        isNewClient = true
      }

      const nowIso = new Date().toISOString()
      const tags = formData.linkedCaseId && selectedCase
        ? [...formData.tags, `case:${selectedCase.caseNumber}`]
        : formData.tags

      const submitData: CreateInteractionPayload = {
        type: formData.type,
        status: 'completed',
        priority: 'medium',
        subject: formData.subject.trim(),
        description: formData.notes.trim() || undefined,
        completedAt: nowIso,
        updatedAt: nowIso,
        tags
      }

      await clientService.createInteraction(clientId, submitData)

      // Mostrar modal de sucesso em vez de redirecionar
      setSavedClientId(clientId)
      setWasNewClientCreated(isNewClient)
      setShowSuccessModal(true)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar atendimento'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      {/* Pós-salvamento: próximos passos (sem fechar por fora, como antes) */}
      <Dialog open={showSuccessModal && !!savedClientId} onOpenChange={() => { /* só pelas ações */ }}>
        <DialogContent
          showCloseButton={false}
          fullScreenOnMobile
          className="max-sm:overflow-y-auto"
          {...(wasNewClientCreated ? {} : { 'aria-describedby': undefined })}
        >
          <DialogHeader className="items-center text-center">
            <CheckCircle className="size-10 text-muted-foreground" aria-hidden="true" />
            <DialogTitle className="text-xl">Atendimento Registrado!</DialogTitle>
            {wasNewClientCreated && (
              <DialogDescription>
                Um novo contato foi criado. Complete os dados para melhor gerenciamento.
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="flex flex-col gap-2">
            {wasNewClientCreated && (
              <Button onClick={() => navigate(`/clients/${savedClientId}/edit`)}>
                <User className="size-4" /> Completar Dados do Contato
              </Button>
            )}
            <Button variant={wasNewClientCreated ? 'outline' : 'default'} onClick={() => navigate(`/cases/new?clientId=${savedClientId}`)}>
              <FileText className="size-4" /> Converter em Caso
            </Button>
            <Button variant="outline" onClick={() => navigate(`/cases/new?clientId=${savedClientId}&isLegal=true`)}>
              <Scale className="size-4" /> Converter em Processo Judicial
            </Button>
            <Button variant="ghost" onClick={() => navigate(`/clients/${savedClientId}`)}>
              Ver Detalhes do Contato
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <FormPageHeader
        title="Novo Atendimento"
        description="Registre um novo atendimento ao contato"
        icon={MessageSquare}
        backTo="/clients"
        backLabel="Voltar"
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <FormSection title="Registro de Atendimento" icon={MessageSquare}>
          <FormField label="Contato *" htmlFor={selectedClient ? undefined : 'interaction-client'}>
            {selectedClient ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 p-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{getClientDisplayName(selectedClient)}</p>
                  <p className="text-sm text-muted-foreground">#{selectedClient.clientNumber}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remover contato selecionado"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => {
                    setSelectedClient(null)
                    setClientNameInput('')
                    setFormData(prev => ({ ...prev, clientId: '' }))
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="relative">
                  <Input
                    id="interaction-client"
                    type="text"
                    autoComplete="off"
                    placeholder="Digite o nome do contato..."
                    value={clientNameInput}
                    onChange={(e) => {
                      setClientNameInput(e.target.value)
                      setShowClientDropdown(true)
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                  />
                  {showClientDropdown && clientNameInput && filteredClients.length > 0 && (
                    <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
                      {filteredClients.map(client => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleClientSelect(client)}
                          className="w-full px-3 py-2 text-left hover:bg-muted"
                        >
                          <p className="truncate text-sm font-medium">{getClientDisplayName(client)}</p>
                          <p className="text-xs text-muted-foreground">#{client.clientNumber}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {clientNameInput && filteredClients.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Nenhum contato encontrado. Um novo contato será criado com o nome &quot;{clientNameInput}&quot;.
                  </p>
                )}
              </div>
            )}
          </FormField>

          <FormField label="Tipo de Atendimento *" htmlFor="interaction-type">
            <NativeSelect
              id="interaction-type"
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
            >
              {Object.entries(interactionTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </NativeSelect>
          </FormField>

          <FormField label="Assunto *" htmlFor="interaction-subject">
            <Input
              id="interaction-subject"
              type="text"
              required
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Ex: Consulta inicial sobre processo trabalhista"
            />
          </FormField>

          <FormField label="Registro do atendimento *" htmlFor="interaction-notes">
            <Textarea
              id="interaction-notes"
              rows={8}
              required
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Descreva os detalhes do atendimento, discussões realizadas e resultados obtidos..."
            />
          </FormField>

          <FormField label="Vincular a caso ou processo (opcional)" htmlFor={selectedCase ? undefined : 'interaction-case'}>
            {selectedCase ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 p-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{selectedCase.title}</p>
                  <p className="text-sm text-muted-foreground">#{selectedCase.caseNumber}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remover caso vinculado"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => {
                    setSelectedCase(null)
                    setCaseSearchInput('')
                    setFormData(prev => ({ ...prev, linkedCaseId: '' }))
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  id="interaction-case"
                  type="text"
                  autoComplete="off"
                  placeholder="Buscar caso por número ou título..."
                  value={caseSearchInput}
                  onChange={(e) => {
                    setCaseSearchInput(e.target.value)
                    setShowCaseDropdown(true)
                  }}
                  onFocus={() => setShowCaseDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCaseDropdown(false), 200)}
                />
                {showCaseDropdown && filteredCases.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
                    {filteredCases.slice(0, 10).map(caseItem => (
                      <button
                        key={caseItem.id}
                        type="button"
                        onClick={() => handleCaseSelect(caseItem)}
                        className="w-full px-3 py-2 text-left hover:bg-muted"
                      >
                        <p className="truncate text-sm font-medium">{caseItem.title}</p>
                        <p className="text-xs text-muted-foreground">#{caseItem.caseNumber}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </FormField>

          <FormField label="Etiquetas (opcional)" htmlFor="interaction-tags">
            <TagsInput
              id="interaction-tags"
              tags={formData.tags}
              onAdd={handleAddTag}
              onRemove={handleRemoveTag}
              placeholder="Digite uma etiqueta e pressione Enter"
            />
          </FormField>
        </FormSection>

        {error && (
          <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <FormActions>
          <Button variant="outline" asChild>
            <Link to="/clients">Cancelar</Link>
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Salvar Atendimento
          </Button>
        </FormActions>
      </form>
    </div>
  )
}
